import { Type } from '@angular/core';
import { ServiceRequestStaticFormHost } from '../pages/service-request-form-page/service-request-static-form.host';

export interface ServiceRequestFormDefinition {
  formCode: string;
  formName: string;
  formId: number;
  allowsQuantity?: boolean;
  isPlaceholderCode?: boolean;
  figmaNodeId?: string;
  /** Lazy loader for static form component — omitted until Figma + static component exist */
  component?: () => Promise<Type<ServiceRequestStaticFormHost>>;
}

export const SERVICE_REQUEST_FORMS: Record<string, ServiceRequestFormDefinition> = {
  PP07: {
    formCode: 'PP07',
    formName: 'Propane Tank and PRV Inspection',
    formId: 5,
    allowsQuantity: true,
    figmaNodeId: '6279:192775',
    component: () =>
      import('../pages/service-request-form-page/components/forms/pp07/pp07-form.component').then(
        (m) => m.Pp07FormComponent,
      ),
  },
  PP01: {
    formCode: 'PP01',
    formName: 'Propane Gas Check',
    formId: 3,
    figmaNodeId: '7325:199030',
    component: () =>
      import('../pages/service-request-form-page/components/forms/pp01/pp01-form.component').then(
        (m) => m.Pp01FormComponent,
      ),
  },
  PP03: {
    formCode: 'PP03',
    formName: 'DOT/Cylinder Information Update',
    formId: 2103,
    figmaNodeId: '7317:194369',
    component: () =>
      import('../pages/service-request-form-page/components/forms/pp03/pp03-form.component').then(
        (m) => m.Pp03FormComponent,
      ),
  },
  'PP-DISP': {
    formCode: 'PP-DISP',
    formName: 'Dispenser Inspection',
    formId: 2112,
    isPlaceholderCode: true,
    figmaNodeId: '7204:187147',
    component: () =>
      import('../pages/service-request-form-page/components/forms/pp-disp/pp-disp-form.component').then(
        (m) => m.PpDispFormComponent,
      ),
  },
  'PP-API': {
    formCode: 'PP-API',
    formName: 'API Annual Propane Plant Maintenance/Inspection',
    formId: 2113,
    isPlaceholderCode: true,
    figmaNodeId: '7297:190802',
    component: () =>
      import('../pages/service-request-form-page/components/forms/pp-api/pp-api-form.component').then(
        (m) => m.PpApiFormComponent,
      ),
  },
  PP06: {
    formCode: 'PP06',
    formName: 'Propane Vaporizer Inspection',
    formId: 4,
    allowsQuantity: true,
    figmaNodeId: '7441:74838',
    component: () =>
      import('../pages/service-request-form-page/components/forms/pp06/pp06-form.component').then(
        (m) => m.Pp06FormComponent,
      ),
  },
  PP08: {
    formCode: 'PP08',
    formName: 'Mobile Food Service Equipment Inspection',
    formId: 6,
    figmaNodeId: '7496:97012',
    component: () =>
      import('../pages/service-request-form-page/components/forms/pp08/pp08-form.component').then(
        (m) => m.Pp08FormComponent,
      ),
  },
  PP09: {
    formCode: 'PP09',
    formName: 'Monthly Bulk Plant Maintenance/Inspection',
    formId: 2109,
    figmaNodeId: '7506:101006',
    component: () =>
      import('../pages/service-request-form-page/components/forms/pp09/pp09-form.component').then(
        (m) => m.Pp09FormComponent,
      ),
  },
  PP24: {
    formCode: 'PP24',
    formName: '24 Pro Plus',
    formId: 2124,
    figmaNodeId: '7533:108363',
    component: () =>
      import('../pages/service-request-form-page/components/forms/pp24/pp24-form.component').then(
        (m) => m.Pp24FormComponent,
      ),
  },
  PP11: {
    formCode: 'PP11',
    formName: 'Bulk Plan Modification Request',
    formId: 2111,
  },
  SA01: {
    formCode: 'SA01',
    formName: 'New Residential Account Form',
    formId: 7,
  },
  SE01: {
    formCode: 'SE01',
    formName: 'Field Service Invoice',
    formId: 1,
  },
  SE02: {
    formCode: 'SE02',
    formName: 'Quote Form',
    formId: 2,
  },
  SE06: {
    formCode: 'SE06',
    formName: 'Site Safety Assessment - Stephensons',
    formId: 2306,
    figmaNodeId: '7461:79206',
    component: () =>
      import('../pages/service-request-form-page/components/forms/se06/se06-form.component').then(
        (m) => m.Se06FormComponent,
      ),
  },
  CI: {
    formCode: 'CI',
    formName: 'Large Storage Vessel Installation / Commissioning Inspection',
    formId: 2310,
    figmaNodeId: '8471:117012',
    component: () =>
      import('../pages/service-request-form-page/components/forms/ci/ci-form.component').then(
        (m) => m.CiFormComponent,
      ),
  },
  HI: {
    formCode: 'HI',
    formName: 'Propane Hose Inspection Report',
    formId: 2311,
    figmaNodeId: '8471:126835',
    component: () =>
      import('../pages/service-request-form-page/components/forms/hi/hi-form.component').then(
        (m) => m.HiFormComponent,
      ),
  },
  VENTI: {
    formCode: 'VENTI',
    formName: 'Large Storage Vessel Visual External Non-Thorough',
    formId: 19,
    figmaNodeId: '8479:133251',
    component: () =>
      import('../pages/service-request-form-page/components/forms/venti/venti-form.component').then(
        (m) => m.VentiFormComponent,
      ),
  },
  SE08: {
    formCode: 'SE08',
    formName: 'Company Asset Info Update',
    formId: 2308,
  },
  DE02: {
    formCode: 'DE02',
    formName: 'Asset Movement',
    formId: 8,
  },
};

export const STATIC_SERVICE_REQUEST_FORM_CODES = Object.values(SERVICE_REQUEST_FORMS)
  .filter((definition) => !!definition.component)
  .map((definition) => definition.formCode);

export const SUPPORTED_SERVICE_REQUEST_FORM_CODES = Object.values(SERVICE_REQUEST_FORMS).map(
  (definition) => definition.formCode,
);

/** Legacy catalogue ids from mock/offline bundles before API id alignment. */
const LEGACY_SERVICE_REQUEST_FORM_IDS: Record<number, string> = {
  2101: 'PP01',
  2107: 'PP07',
  2103: 'PP03',
  2106: 'PP06',
  2108: 'PP08',
  2109: 'PP09',
  2124: 'PP24',
  2112: 'PP-DISP',
  2113: 'PP-API',
  2306: 'SE06',
  2310: 'CI',
  2311: 'HI',
  19: 'VENTI',
  2201: 'SA01',
  2301: 'SE01',
  2302: 'SE02',
  2402: 'DE02',
  2111: 'PP11',
  2308: 'SE08',
};

const FORM_BY_ID = new Map(
  Object.values(SERVICE_REQUEST_FORMS).map((definition) => [definition.formId, definition]),
);

export function getServiceRequestFormById(
  formId: number,
  formCode?: string,
): ServiceRequestFormDefinition | undefined {
  const direct = FORM_BY_ID.get(formId);
  if (direct) {
    return direct;
  }

  const legacyCode = LEGACY_SERVICE_REQUEST_FORM_IDS[formId];
  if (legacyCode) {
    return SERVICE_REQUEST_FORMS[legacyCode];
  }

  if (formCode) {
    return getServiceRequestFormByCode(formCode);
  }

  return undefined;
}

export function getServiceRequestFormByCode(formCode: string): ServiceRequestFormDefinition | undefined {
  return SERVICE_REQUEST_FORMS[formCode];
}

export function resolveServiceRequestFormDefinition(
  formCode?: string,
  formId?: number,
): ServiceRequestFormDefinition | undefined {
  if (formCode) {
    const byCode = getServiceRequestFormByCode(formCode);
    if (byCode) {
      return byCode;
    }
  }

  if (formId != null) {
    return getServiceRequestFormById(formId, formCode);
  }

  return undefined;
}

export function isSupportedServiceRequestFormCode(formCode: string): boolean {
  return !!getServiceRequestFormByCode(formCode);
}

export function isSupportedServiceRequestFormId(formId: number, formCode?: string): boolean {
  return !!resolveServiceRequestFormDefinition(formCode, formId);
}

export function buildServiceRequestFormCatalogRows(): {
  formId: number;
  formCode: string;
  formName: string;
  allowsQuantity?: boolean;
}[] {
  return Object.values(SERVICE_REQUEST_FORMS).map((definition) => ({
    formId: definition.formId,
    formCode: definition.formCode,
    formName: definition.formName,
    allowsQuantity: definition.allowsQuantity,
  }));
}
